import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  App,
  Button,
  Card,
  Descriptions,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  CustomerServiceOutlined,
  ReloadOutlined,
  StopOutlined,
  DeleteOutlined,
  PictureOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { axios } from 'lib/axios';
import { API_URL } from 'config';
import storage from 'utils/storage';
import PageShell, { useBrandToken } from 'components/PageShell';
import { fmtDate } from 'lib/adminDetail';
import {
  subscribeTicketSocket,
  joinAdminTicketRoom,
  leaveAdminTicketRoom,
  connectTicketSocket,
} from 'lib/ticketSocket';

const { Text } = Typography;

const statusColor = {
  OPEN: 'gold',
  IN_PROGRESS: 'blue',
  WAITING_MERCHANT: 'purple',
  RESOLVED: 'green',
  CLOSED: 'default',
};

const TicketsPage = () => {
  const { message } = App.useApp();
  const colors = useBrandToken();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState();
  const [category, setCategory] = useState();
  const [open, setOpen] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState('');
  const [saving, setSaving] = useState(false);
  const [pendingMedia, setPendingMedia] = useState(null);
  const [uploading, setUploading] = useState(false);
  const openTicketIdRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchTickets = useCallback(async (opts = {}) => {
    const quiet = !!opts.quiet;
    if (!quiet) setLoading(true);
    try {
      const res = await axios.get('/admin/tickets', {
        params: {
          ...(search.trim() ? { q: search.trim() } : {}),
          ...(status ? { status } : {}),
          ...(category ? { category } : {}),
        },
      });
      const list = res?.data || [];
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      if (!quiet) setRows([]);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [search, status, category]);

  const reloadOpenTicket = useCallback(async (id) => {
    if (!id) return;
    try {
      const res = await axios.get(`/admin/tickets/${id}`);
      if (res?.data) setTicket(res.data);
    } catch (_) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    fetchTickets();
    // Search is applied via Refresh / Enter; filters auto-load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, category]);

  // WebSocket: inbox + open thread (no polling).
  useEffect(() => {
    connectTicketSocket();
    const unsub = subscribeTicketSocket((event, payload) => {
      if (event !== 'ticketUpdated') return;
      const ticketId = payload?.ticketId;
      fetchTickets({ quiet: true });
      if (
        ticketId &&
        openTicketIdRef.current &&
        String(ticketId) === String(openTicketIdRef.current)
      ) {
        reloadOpenTicket(ticketId);
      }
    });
    return () => {
      unsub();
      leaveAdminTicketRoom();
    };
  }, [fetchTickets, reloadOpenTicket]);

  const merchant = (t) =>
    t?.merchantId && typeof t.merchantId === 'object' ? t.merchantId : {};

  const openTicket = async (record) => {
    setOpen(true);
    setTicket(record);
    setReply('');
    openTicketIdRef.current = record?._id || null;
    joinAdminTicketRoom(record?._id);
    try {
      const res = await axios.get(`/admin/tickets/${record._id}`);
      setTicket(res?.data || record);
    } catch (e) {
      // interceptor toast
    }
  };

  const closeTicketModal = () => {
    leaveAdminTicketRoom(openTicketIdRef.current);
    openTicketIdRef.current = null;
    setOpen(false);
    setTicket(null);
    setReply('');
    setPendingMedia(null);
  };

  const uploadChatMedia = async (file) => {
    const form = new FormData();
    form.append('file', file);
    const token = storage.getToken();
    const res = await fetch(`${API_URL}/chat/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || !body?.success) {
      throw new Error(body?.message || 'Upload failed');
    }
    return body.data;
  };

  const onPickMedia = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      message.error('Image must be 5MB or smaller');
      return;
    }
    setUploading(true);
    try {
      const data = await uploadChatMedia(file);
      setPendingMedia({
        url: data.url,
        messageType: data.messageType || 'image',
        mimeType: data.mimeType || file.type,
        previewName: file.name,
      });
      message.success('Screenshot attached');
    } catch (err) {
      message.error(err?.message || 'Could not upload image');
    } finally {
      setUploading(false);
    }
  };

  const sendReply = async () => {
    const text = reply.trim();
    if (!ticket?._id || (!text && !pendingMedia?.url)) return;
    if (ticket.status === 'RESOLVED' || ticket.status === 'CLOSED') {
      message.warning('Reopen the ticket before sending a reply');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`/admin/tickets/${ticket._id}/messages`, {
        ...(text ? { message: text } : {}),
        ...(pendingMedia?.url
          ? {
              mediaUrl: pendingMedia.url,
              messageType: pendingMedia.messageType || 'image',
              mimeType: pendingMedia.mimeType,
            }
          : {}),
      });
      setTicket(res?.data || ticket);
      setReply('');
      setPendingMedia(null);
      message.success('Reply sent');
      fetchTickets({ quiet: true });
    } finally {
      setSaving(false);
    }
  };

  const setTicketStatus = async (next) => {
    if (!ticket?._id) return;
    setSaving(true);
    try {
      const res = await axios.patch(`/admin/tickets/${ticket._id}/status`, {
        status: next,
      });
      setTicket(res?.data || ticket);
      message.success('Status updated');
      fetchTickets({ quiet: true });
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async () => {
    const m = merchant(ticket);
    if (!m._id) return;
    setSaving(true);
    try {
      await axios.post(`/admin/merchants/${m._id}/deactivate`, {
        ticketId: ticket._id,
      });
      message.success('Merchant deactivated');
      closeTicketModal();
      fetchTickets();
    } finally {
      setSaving(false);
    }
  };

  const hardDelete = async () => {
    const m = merchant(ticket);
    if (!m._id) return;
    setSaving(true);
    try {
      await axios.delete(`/admin/merchants/${m._id}`);
      await axios.patch(`/admin/tickets/${ticket._id}/status`, {
        status: 'CLOSED',
        adminNote: 'Account deleted after discussion',
      });
      message.success('Merchant deleted');
      closeTicketModal();
      fetchTickets();
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'Ticket',
      dataIndex: 'ticketNo',
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <strong>{v || '—'}</strong>
          <span style={{ color: colors.muted, fontSize: 12 }}>{r.subject}</span>
        </Space>
      ),
    },
    {
      title: 'Merchant',
      key: 'merchant',
      render: (_, r) => {
        const m = merchant(r);
        return (
          <Space direction="vertical" size={0}>
            <span>{m.full_name || '—'}</span>
            <span style={{ color: colors.muted, fontSize: 12 }}>
              {m.mobile_number || ''}
            </span>
          </Space>
        );
      },
    },
    {
      title: 'Category',
      dataIndex: 'category',
      render: (v) => (v || '').replaceAll('_', ' '),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (v) => <Tag color={statusColor[v] || 'default'}>{v}</Tag>,
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      render: (v) => fmtDate(v),
    },
    {
      title: '',
      key: 'open',
      render: (_, r) => (
        <Button type="primary" size="small" onClick={() => openTicket(r)}>
          Open chat
        </Button>
      ),
    },
  ];

  const messages = ticket?.messages || [];
  const m = merchant(ticket);
  const isDeactivation = ticket?.category === 'ACCOUNT_DEACTIVATION';
  const isClosed =
    ticket?.status === 'RESOLVED' || ticket?.status === 'CLOSED';

  const filteredHint = useMemo(
    () => `${rows.length} ticket${rows.length === 1 ? '' : 's'}`,
    [rows.length],
  );

  return (
    <PageShell
      title="Support tickets"
      description="Merchant queries and deactivation requests. Chat first, then deactivate or delete."
      actions={
        <Button icon={<ReloadOutlined />} onClick={fetchTickets} loading={loading}>
          Refresh
        </Button>
      }
    >
      <Card style={{ borderRadius: 12, border: `1px solid ${colors.border}` }}>
        <Space wrap style={{ marginBottom: 16 }}>
          <Input.Search
            placeholder="Search ticket no or subject"
            allowClear
            style={{ width: 280 }}
            onSearch={(v) => {
              setSearch(v);
              setTimeout(fetchTickets, 0);
            }}
          />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 180 }}
            value={status}
            onChange={setStatus}
            options={[
              { value: 'OPEN', label: 'Open' },
              { value: 'IN_PROGRESS', label: 'In chat' },
              { value: 'WAITING_MERCHANT', label: 'Waiting merchant' },
              { value: 'RESOLVED', label: 'Resolved' },
              { value: 'CLOSED', label: 'Closed' },
            ]}
          />
          <Select
            allowClear
            placeholder="Category"
            style={{ width: 220 }}
            value={category}
            onChange={setCategory}
            options={[
              { value: 'ACCOUNT_DEACTIVATION', label: 'Account deactivation' },
              { value: 'GENERAL', label: 'General' },
              { value: 'BILLING', label: 'Billing' },
              { value: 'TECHNICAL', label: 'Technical' },
              { value: 'OUTLET', label: 'Outlet' },
              { value: 'OTHER', label: 'Other' },
            ]}
          />
          <Text type="secondary">{filteredHint}</Text>
        </Space>
        <Table
          rowKey={(r) => r._id}
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={{ pageSize: 12 }}
          onRow={(record) => ({
            onClick: () => openTicket(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <Modal
        title={ticket?.ticketNo || 'Ticket'}
        open={open}
        onCancel={closeTicketModal}
        width={760}
        footer={null}
      >
        {ticket && (
          <>
            <Descriptions size="small" bordered column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Merchant">{m.full_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Mobile">{m.mobile_number || '—'}</Descriptions.Item>
              <Descriptions.Item label="Category" span={2}>
                {(ticket.category || '').replaceAll('_', ' ')}
              </Descriptions.Item>
              <Descriptions.Item label="Subject" span={2}>
                {ticket.subject}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={statusColor[ticket.status]}>{ticket.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Account">
                {m.active === false ? 'Inactive' : 'Active'}
              </Descriptions.Item>
            </Descriptions>

            <div
              style={{
                maxHeight: 280,
                overflowY: 'auto',
                background: '#f6f7fb',
                padding: 12,
                borderRadius: 10,
                marginBottom: 12,
              }}
            >
              {messages.map((msg, i) => {
                const admin = msg.senderRole === 'SUPER ADMIN';
                const mediaUrl = msg.mediaUrl || '';
                const body = msg.message || '';
                const showText =
                  body && body !== '[Screenshot]' && body !== '[Media]';
                return (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      justifyContent: admin ? 'flex-end' : 'flex-start',
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        maxWidth: '80%',
                        background: admin ? colors.primary || '#0B2545' : '#fff',
                        color: admin ? '#fff' : '#111',
                        padding: '8px 12px',
                        borderRadius: 12,
                      }}
                    >
                      <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 4 }}>
                        {admin ? 'You' : 'Merchant'} · {fmtDate(msg.createdAt)}
                      </div>
                      {mediaUrl ? (
                        <a
                          href={mediaUrl}
                          target="_blank"
                          rel="noreferrer"
                          style={{ display: 'block', marginBottom: showText ? 8 : 0 }}
                        >
                          <img
                            src={mediaUrl}
                            alt="Attachment"
                            style={{
                              maxWidth: '100%',
                              maxHeight: 220,
                              borderRadius: 8,
                              display: 'block',
                            }}
                          />
                        </a>
                      ) : null}
                      {showText || !mediaUrl ? body : null}
                    </div>
                  </div>
                );
              })}
            </div>

            {isClosed ? (
              <div
                style={{
                  padding: 14,
                  borderRadius: 10,
                  background: colors.accentSoft || '#E6F7F0',
                  border: `1px solid ${colors.border}`,
                }}
              >
                <Text style={{ display: 'block', marginBottom: 10 }}>
                  This ticket is{' '}
                  <strong>{ticket.status === 'CLOSED' ? 'closed' : 'resolved'}</strong>
                  . Replies are disabled.
                </Text>
                <Space wrap>
                  <Button
                    type="primary"
                    loading={saving}
                    onClick={() => setTicketStatus('IN_PROGRESS')}
                  >
                    Reopen ticket
                  </Button>
                  {ticket.status === 'RESOLVED' && (
                    <Button
                      loading={saving}
                      onClick={() => setTicketStatus('CLOSED')}
                    >
                      Close permanently
                    </Button>
                  )}
                </Space>
              </div>
            ) : (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  style={{ display: 'none' }}
                  onChange={onPickMedia}
                />
                {pendingMedia?.url ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      marginBottom: 10,
                      padding: 8,
                      borderRadius: 8,
                      border: `1px solid ${colors.border}`,
                      background: '#fff',
                    }}
                  >
                    <img
                      src={pendingMedia.url}
                      alt="Pending"
                      style={{
                        width: 56,
                        height: 56,
                        objectFit: 'cover',
                        borderRadius: 6,
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text ellipsis style={{ display: 'block' }}>
                        {pendingMedia.previewName || 'Screenshot'}
                      </Text>
                      <Button
                        type="link"
                        danger
                        size="small"
                        icon={<CloseCircleOutlined />}
                        onClick={() => setPendingMedia(null)}
                        style={{ padding: 0 }}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : null}
                <Input.TextArea
                  rows={3}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Reply to merchant (optional caption with screenshot)…"
                  style={{ marginBottom: 10 }}
                />
                <Space wrap>
                  <Button
                    icon={<PictureOutlined />}
                    loading={uploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Attach screenshot
                  </Button>
                  <Button
                    type="primary"
                    icon={<CustomerServiceOutlined />}
                    loading={saving}
                    disabled={!reply.trim() && !pendingMedia?.url}
                    onClick={sendReply}
                  >
                    Send reply
                  </Button>
                  <Button onClick={() => setTicketStatus('WAITING_MERCHANT')}>
                    Wait for merchant
                  </Button>
                  <Button onClick={() => setTicketStatus('RESOLVED')}>
                    Resolve
                  </Button>
                  {isDeactivation && (
                    <Popconfirm
                      title="Deactivate this merchant?"
                      description="They cannot sign in. Stores stay until you delete. Only after you have discussed the ticket."
                      okText="Deactivate"
                      onConfirm={deactivate}
                    >
                      <Button danger icon={<StopOutlined />} loading={saving}>
                        Deactivate account
                      </Button>
                    </Popconfirm>
                  )}
                  {isDeactivation && (
                    <Popconfirm
                      title="Permanently delete this merchant?"
                      description="Deletes the account and stores. Use only after discussion."
                      okText="Delete"
                      okButtonProps={{ danger: true }}
                      onConfirm={hardDelete}
                    >
                      <Button danger icon={<DeleteOutlined />} loading={saving}>
                        Delete account
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              </>
            )}
          </>
        )}
      </Modal>
    </PageShell>
  );
};

export default TicketsPage;
